import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {inboxHasColor} from '../../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {TableCell} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCell';
import {
  TableCellProps,
  TableCellStyleProps,
} from '../../../../../libs/shared/ui/src/components/tables/cells/tableCellTypes';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
} from '../../../../../libs/shared/ui/src/components/tables/helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../../../../../libs/shared/ui/src/components/tables/tableColumn';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Styles.
 */

const StyledInboxCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledInboxCellValueDivProps {
  $isSelected: boolean;
}
const StyledInboxCellNameDiv = styled.div<StyledInboxCellValueDivProps>`
  line-height: 16px;
  flex: 1;
  margin-left: 8px;
  ${ellipsis()};

  ${(p) => getCellTextColor(p.$isSelected)}
`;

/*
 * Component.
 */

export const InboxCell = (props: TableCellProps<InboxModel>): ReactElement => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;
  const color = props.value && inboxHasColor(props.value) ? props.value.color : theme.greys.shade50;

  return (
    <TableCell<InboxModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledInboxCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <Skeleton width="8px" height="8px" borderRadius="50%" scheme={settings?.loading?.style} />
          <StyledInboxCellNameDiv $isSelected={false}>
            <Skeleton height="16px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledInboxCellNameDiv>
        </StyledInboxCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledInboxCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <ColorDot color={color} size={8} />
          <StyledInboxCellNameDiv $isSelected={Boolean(isSelected)}>{value.name}</StyledInboxCellNameDiv>
        </StyledInboxCellContainerDiv>
      )}
    />
  );
};
